import React from "react";
import { useTranslation } from "react-i18next";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import BankForm from "./BankForm";

/**
 * `BankForm` in its `<Elements>` provider.
 *
 * `BankForm` calls `useStripe()`, which throws "Could not find Elements
 * context" the moment it is rendered without this wrapper — so this component,
 * not `BankForm`, is what a page mounts.
 *
 * The Stripe instance is a MODULE-LEVEL singleton: `loadStripe` injects a
 * script tag, and calling it per mount re-runs that work every time the billing
 * section is opened. `Elements` takes the promise directly, so the provider is
 * mounted on the first render and children never wait for a state update —
 * `useStripe()` simply returns null until it resolves.
 */

let stripePromise: Promise<Stripe | null> | null = null;

/** The one Stripe instance, or null when no publishable key is configured. */
export const getStripePromise = (): Promise<Stripe | null> | null => {
  const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!stripeKey) return null;
  if (!stripePromise) stripePromise = loadStripe(stripeKey);
  return stripePromise;
};

const BankFormItems: React.FC = () => {
  const { t } = useTranslation();
  const stripe = getStripePromise();

  if (!stripe) {
    return (
      <p className="text-sm text-danger-500">
        {t('Payments are unavailable — Stripe is not configured for this environment.')}
      </p>
    );
  }

  return (
    <Elements stripe={stripe}>
      <BankForm />
    </Elements>
  );
};

export default BankFormItems;
